/**
 * Copying from the GM's library into the session, several at once (the user, 2026-10-20: "it doesn't let importing
 * in bulk"): maps into the session's maps, characters into its character library.
 *
 * Each picked row is read and copied in turn, and ONE toast says what landed (one more names what did not, and why):
 * a row that cannot be read never stops the rest. A copy keeps apart from the batch's EARLIER copies as well as from
 * what the session held when the batch began - the shell's lists are a render behind until the batch is done, so
 * two maps called "Docks" would otherwise take one name, and two sheets with one id overwrite each other. A single
 * map is also loaded onto the table, as before; several are only copied (the table holds one map).
 */
import { readCharacterAsset, readMapAsset, type LibraryAccess } from '../assetSession';
import { copyIdFor } from '../engine/assets/provenance';
import { uniqueName } from '../engine/assets/tree';
import type { AssetRow } from '../engine/assets/types';
import type { LibraryEntry } from '../engine/battlefield/mapLibrarySync';
import type { Terrain } from '../engine/battlefield/presets';
import type { Character } from '../engine/character/types';
import { mintId } from '../utils/mintUnique';
import { diag, errorSummary } from '../diagnostics/log';

type Toast = (message: string, opts?: { type?: 'info' | 'error' | 'success' }) => void;

/** What the copies need of the shell: the session's maps and characters, and the toast. */
export interface CopyShell {
  library: LibraryEntry[];
  /** One entry, or several in ONE write (the shell's list is a render behind until the batch is done). */
  copyMap(entry: LibraryEntry | LibraryEntry[]): void;
  loadMapWithRules(terrain: Terrain): void;
  pushToast: Toast;
  /** The session's character library (the sync's). */
  sync?: { sessionCharacters: { character: Character }[]; pushCharacter(character: Character): void } | null;
}

export const sessionSheets = (s: CopyShell): Character[] => s.sync?.sessionCharacters.map((e) => e.character) ?? [];

/** Push a sheet into the session's character library, or say why it cannot be. */
export function pushSheet(s: CopyShell, sheet: Character): void {
  if (!s.sync) throw new Error('This session has no character library to copy into.');
  s.sync.pushCharacter(sheet);
}

const messageOf = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** Copy each row in turn (`copyOne` says what it copied, "Docks v2"); one toast for what landed, one for what not. */
async function copyAll(toast: Toast, rows: AssetRow[], into: string, copyOne: (row: AssetRow) => Promise<string>) {
  const copied: string[] = [];
  const failed: string[] = [];
  for (const row of rows) {
    try {
      copied.push(await copyOne(row));
    } catch (err) {
      diag.error('assets', `copying ${row.name} failed`, errorSummary(err));
      failed.push(`${row.name} (${messageOf(err)})`);
    }
  }
  if (copied.length) toast(`${copied.join(', ')} copied into ${into}.`, { type: 'success' });
  if (failed.length) toast(`Not copied: ${failed.join(', ')}.`, { type: 'error' });
}

/** The name a map's copy takes: its earlier copy's, else the asset's, numbered apart from the session's and `taken`. */
function copyName(s: CopyShell, row: AssetRow, taken: string[]): string {
  const earlier = s.library.find((e) => e.origin?.assetId === row.id)?.name;
  return earlier ?? uniqueName([...s.library.map((e) => e.name), ...taken], row.name);
}

/** Copy maps into the session's map library, in one write; exactly one is also loaded onto the table. */
export async function pickMaps(s: CopyShell, access: LibraryAccess, rows: AssetRow[]): Promise<void> {
  const entries: LibraryEntry[] = [];
  await copyAll(s.pushToast, rows, 'this session', async (row) => {
    const terrain = await readMapAsset(access, row);
    const name = copyName(
      s,
      row,
      entries.map((e) => e.name),
    );
    entries.push({ name, terrain: { ...terrain, name }, origin: { assetId: row.id, version: row.version } });
    return `${name} v${row.version}`;
  });
  if (!entries.length) return;
  s.copyMap(entries);
  if (rows.length === 1) s.loadMapWithRules(entries[0].terrain);
}

/** Copy characters into the session's character library, each under an id no other sheet there holds. */
export function pickCharacters(s: CopyShell, access: LibraryAccess, rows: AssetRow[]): Promise<void> {
  const pushed: Character[] = [];
  return copyAll(s.pushToast, rows, "this session's characters", async (row) => {
    const sheet = await readCharacterAsset(access, row);
    const id = copyIdFor(row.id, sheet.id, [...sessionSheets(s), ...pushed], () => mintId('char'));
    const copy = { ...sheet, id, origin: { assetId: row.id, version: row.version } };
    pushSheet(s, copy);
    pushed.push(copy);
    return `${sheet.name} v${row.version}`;
  });
}
